import React, { useMemo } from "react";
import cx from "classnames";
import Icon from "../Icon";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSelector } from "@/hooks/useSelector";
import { useProfile } from "@/hooks/firestore/v2/user/useProfile";
import { useZendeskDomain } from "@/hooks/firestore/v2/user/useZendesk";
import { Form } from "@/components/Form";
import { Input } from "@/components/Input";
import { Button } from "@/components/Button";
import { TextArea } from "@/components/TextArea";
import * as styles from "./HelpCenter.module.scss";
import { toast } from "hooks/ui/useToast";
import {
  WEB_TO_APP_MESSAGE_TYPES,
  sendAppMessage,
} from "utilities/AppWebMessagingBridge";
import { createATicket } from "utilities/api";
import { isWebView } from "utilities/display";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { convertErrorToToast } from "utilities/errorsParser";
import { attempt } from "utilities/attempt";

type CreateATicketProps = {
  visible: boolean;
  setVisible: (visible: boolean) => void;
};

const makeSchema = (loggedIn: boolean) => {
  return z.object({
    description: z.string().min(1, { message: "Description is required." }),
    subject: z.string().min(1, { message: "Subject is required." }),
    email: loggedIn
      ? z.string().optional()
      : z
          .string({ message: "Email is required." })
          .email("Email must be valid"),
  });
};
export const CreateATicket = ({ visible, setVisible }: CreateATicketProps) => {
  const { token, loggedIn } = useSelector((state) => state.auth);
  const { profile } = useProfile();
  const zendeskDomain = useZendeskDomain();

  const openLinkExternallyOfTheApps = (href: string) => {
    if (isWebView()) {
      sendAppMessage(WEB_TO_APP_MESSAGE_TYPES.APP_MESSAGE_OPEN_EXTERNALLY, {
        url: href,
      });
    } else {
      window.open(href, "_blank");
    }
  };

  const schema = useMemo(() => {
    return makeSchema(loggedIn);
  }, [loggedIn]);

  const {
    handleSubmit,
    register,
    formState: { isSubmitting, errors },
    watch,
  } = useForm({
    resolver: zodResolver(schema),
  });

  return (
    <Form
      onSubmit={handleSubmit(async ({ email, subject, description }) => {
        const fetchResult = await attempt(
          createATicket({
            token,
            payload: { email, subject, description },
          }),
        );

        if (!fetchResult.ok) {
          toast(
            convertErrorToToast(fetchResult.error, {
              title: "Something went wrong",
            }),
          );
          return;
        }

        toast({
          description: "Your request has been submitted.",
          variant: "success",
        });
        setVisible(false);
      })}
    >
      <div className={cx(styles.helpCenter, { [styles.visible]: visible })}>
        {isSubmitting && <span className={styles.loader}></span>}
        <div className={styles.header}>
          <div className={styles.title} onClick={() => setVisible(false)}>
            <div className={styles.titleText}>Picklebet Help Center</div>
            <div className={styles.icon}>
              <Icon type="close" />
            </div>
          </div>
        </div>
        <div className={styles.formContent}>
          <div>
            <Input
              label="Subject"
              {...register("subject")}
              errorMessage={errors?.subject?.message as string}
            />
            {!loggedIn && (
              <Input
                label="Email"
                {...register("email")}
                errorMessage={errors?.email?.message as string}
              />
            )}
            <TextArea
              label="Description"
              {...register("description")}
              errorMessage={errors?.description?.message as string}
            />

            <div className={styles.uploadDocuments}>
              <div>
                Need to{" "}
                <span
                  className={styles.greenLink}
                  onClick={() =>
                    openLinkExternallyOfTheApps(
                      `https://${zendeskDomain}/hc/en-us/requests/new?subject=${watch(
                        "subject",
                      )}&description=${watch(
                        "description",
                      )}&anonymous_requester_email=${
                        profile?.email ?? watch("email")
                      }`,
                    )
                  }
                >
                  {"  "}Upload Documents?
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className={cx(styles.footer, styles.formFooter)}>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={isSubmitting}
          >
            Create a ticket
          </Button>
        </div>
      </div>
    </Form>
  );
};
